import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import LabelField from "../Form/FormFields/LabelField";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import CustomModal from "../ui/CustomModal";
import CustomHookForRuralDetails from "../ruralDetailsCount/CustomHookForRuralDetails";
import { setPidChartId } from "../applicationStatus/store";
import CustomTable from "../ui/CustomTable";
import Accordion from "../ui/Accordion";
import { pieChartConfigGenerator } from "./ChartConfigGenerator";
import NoteComponent from "../ui/table/Notes";

interface DynamicPieChartProps {
  chartListData: {
    id: string;
    title: string;
  };
  width?: string;
}

const DynamicPieChart: React.FC<DynamicPieChartProps> = ({
  chartListData,
  width,
}) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { ruralDetailsCount } = CustomHookForRuralDetails();
  const selector = useSelector((state: any) => state);
  const selectorData = useSelector((state: any) => state?.dynamicData);
  const selectCompontData =
    selector.chartListDetails?.componentData?.ALL?.[chartListData?.id];
  const pieChartData = selectorData[chartListData?.id] || [];
  const isLoading = selectorData.loaders[`report/chart/${chartListData?.id}`];
  const isChartReady =
    pieChartData &&
    Array.isArray(pieChartData?.label_name) &&
    pieChartData?.label_name?.length > 0 &&
    Array.isArray(pieChartData?.data) &&
    pieChartData?.data?.length > 0;
  const notes = pieChartData?.note;
  const { series, categories, chartType, colors } = useMemo(() => {
    if (!isChartReady)
      return { series: [], categories: [], chartType: "pie", colors: [] };
    return pieChartConfigGenerator(pieChartData.data, pieChartData.label_name);
  }, [isChartReady, pieChartData]);

  const chartOptions: any = {
    chart: {
      type: chartType,
      events: {
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          const index = config.dataPointIndex;
          setIsModalOpen(true);
          fetchPopupData((index + 1).toString());
        },
      },
    },
    labels: categories,
    colors,
    fill: {
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.7,
        opacityFrom: 0.9,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    legend: { position: "bottom", fontSize: "14px" },
    tooltip: {
      y: { formatter: (value: number) => `${value.toFixed(2)}%` },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "14px", colors: ["#fff"] },
      formatter: (val: number) => `${val.toFixed(1)}%`,
    },
    responsive: [
      {
        breakpoint: 480,
        options: { chart: { width: 300 }, legend: { position: "bottom" } },
      },
    ],
  };

  const fetchPopupData = async (id?: string) => {
    if (id) {
      await dispatch(setPidChartId(id));
    }
    await ruralDetailsCount(
      selectCompontData?.popup_chart_id,
      chartListData?.id
    );
  };

  useEffect(() => {
    if (selectCompontData?.popup_chart_id) {
      fetchPopupData();
    }
  }, [selectCompontData?.popup_chart_id]);

  return (
    <div className={`grid gap-2 ${width && width}`}>
      <div className="bg-white p-4 rounded-lg w-full ">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <LabelField label={chartListData?.title} />
            {notes ? (
              <NoteComponent notes={notes} modalTitle={chartListData?.title} />
            ) : null}
          </h2>
        </div>
        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <div>
            <Chart
              options={chartOptions}
              series={series}
              type="pie"
              height={295}
            />
            <Accordion
              title="AI Insights"
              componentID={chartListData?.id}
              chartData={chartListData}
            />
          </div>
        )}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="Chart Detail"
        size={(chartListData as any)?.size || "xl"}
      >
        <div>
          <CustomTable
            columns={selectorData?.[selectCompontData?.popup_chart_id]?.columns}
            rows={selectorData[selectCompontData?.popup_chart_id]?.rows}
            notes={selectorData?.[selectCompontData?.popup_chart_id]?.note}
            noteData={
              selectorData?.[selectCompontData?.popup_chart_id]?.remarks
            }
            title="Chart Detail"
            hideTitle={true}
            isLoading={
              selectorData.loaders[
                `report/chart/${selectCompontData?.popup_chart_id}`
              ]
            }
          />
        </div>
      </CustomModal>
    </div>
  );
};
export default DynamicPieChart;
